import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Edit, Trash2, Settings, Search, Filter, CheckCircle, AlertCircle, Camera, X, Eye, MoreVertical, RefreshCw, Battery, ChevronRight, Loader2, FileSpreadsheet, FileDown } from 'lucide-react';
import { formatDateDisplay, formatDateTimeDisplay } from '../utils/dateUtils';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import { useTranslation } from 'react-i18next';
import { SortButton, sortData, DateRangeFilter, SearchFilters } from '../components/DataTableWithFilters';
import { useGroupedRecords, getGroupLabels, getGroupOrder } from '../hooks/useGroupedRecords';
import { GroupHeader } from '../components/GroupHeader';
import { usePermissions } from '../utils/permissionUtils';
import { ScrollableTableContainer } from '../components/ui/scrollable-table-container';
import useDeviceDetection from '../hooks/useDeviceDetection';
import { exportToExcel, exportToPDF } from '../utils/exportUtils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { formatErrorMessage } from '../utils/formatError';
import PlatePair from '../components/PlatePair';

// Helper component to render grouped battery swaps
const BatterySwapsGroupedRecords = ({ records, renderGroupRecords }) => {
  const normalizedRecords = records.map(r => ({
    ...r,
    swap_date: r.swap_date || r.date
  }));
  
  const { groupedRecords, toggleGroup, isGroupCollapsed } = useGroupedRecords(normalizedRecords, 'swap_date');
  const groupLabels = getGroupLabels();
  const groupOrder = getGroupOrder();

  return groupOrder.map(groupKey => {
    const groupRecords = groupedRecords[groupKey];
    if (groupRecords.length === 0) return null;
    
    return (
      <React.Fragment key={groupKey}>
        <TableRow className="border-none">
          <TableCell colSpan={10} className="p-0">
            <GroupHeader
              title={groupLabels[groupKey]}
              count={groupRecords.length}
              groupName={groupKey}
              isCollapsed={isGroupCollapsed(groupKey)}
              onToggle={toggleGroup}
              colorScheme="green"
            />
          </TableCell>
        </TableRow>
        {!isGroupCollapsed(groupKey) && renderGroupRecords(groupRecords, groupKey)}
      </React.Fragment>
    );
  });
};

const BatterySwaps = ({ user }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile, isTablet } = useDeviceDetection();
  
  // Permission checks for module and sub-pages
  const permissions = usePermissions(user, 'batterySwaps');
  const canCreate = permissions.canCreate;
  const canEdit = permissions.canEdit;
  const canDelete = permissions.canDelete;
  const canViewSwapLocations = permissions.canViewSubPage('swapLocations');
  const canCreateSwapLocations = permissions.canCreateSubPage('swapLocations');
  const canEditSwapLocations = permissions.canEditSubPage('swapLocations');
  const canDeleteSwapLocations = permissions.canDeleteSubPage('swapLocations');
  const canViewAlerts = permissions.canViewSubPage('alerts');
  const canEditAlerts = permissions.canEditSubPage('alerts');
  
  const [swaps, setSwaps] = useState([]);
  const [motorcycles, setMotorcycles] = useState([]);
  const [riders, setRiders] = useState([]);
  const [lessees, setLessees] = useState([]);
  const [batteries, setBatteries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const ITEMS_PER_PAGE = 50;
  const [filterOptions, setFilterOptions] = useState({ locations: [], technicians: [] });
  const [exporting, setExporting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [newLocation, setNewLocation] = useState('');
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedSwap, setSelectedSwap] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [users, setUsers] = useState([]);
  const [lastSwapInfo, setLastSwapInfo] = useState(null);
  const [emiratesIdLookup, setEmiratesIdLookup] = useState('');
  const [mismatchDialogOpen, setMismatchDialogOpen] = useState(false);
  // Blocking anomaly dialog (Typo Spike / Missing Reading)
  const [anomalyBlock, setAnomalyBlock] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [mismatchData, setMismatchData] = useState(null);
  const [mismatchForm, setMismatchForm] = useState({ description: '', photos: [] });
  const [alertsDialogOpen, setAlertsDialogOpen] = useState(false);
  const [mismatchAlerts, setMismatchAlerts] = useState([]);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [viewAlertDialogOpen, setViewAlertDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [batteryUpdates, setBatteryUpdates] = useState([]);
  const [batteryLocationOptions, setBatteryLocationOptions] = useState({riders: [], locations: [], statuses: []});
  const [talentStaff, setTalentStaff] = useState([]); // Staff from Talent PMS for technician dropdown
  
  // ODO reading validation state
  const [previousOdoReading, setPreviousOdoReading] = useState(null);
  const [odoWarning, setOdoWarning] = useState(null);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ field: 'date', direction: 'desc' });

  // Helper to get lessee name from lessee_id
  const getLesseeName = (lesseeId) => {
    if (!lesseeId) return null;
    const lessee = lessees.find(l => l.id === lesseeId);
    return lessee?.name || lessee?.company_name || null;
  };

  const renderGroupRecords = (records, groupName) => {
    
    return records.map((swap, index) => (
      <TableRow 
        key={swap.id}
        className={`cursor-pointer hover:bg-green-50 transition-colors duration-150 ${
          index === records.length - 1 ? 'border-b-2 border-slate-200' : 'border-b border-slate-100'
        }`}
        onClick={() => handleViewDetails(swap)}
      >
        <TableCell className="pl-8">
          <div className="flex items-center gap-2">
            <div className="w-1 h-8 bg-gradient-to-b from-green-400 to-green-600 rounded-full"></div>
            <div>
              <div className="font-medium text-slate-800">{formatDateDisplay(swap.swap_date || swap.date) || '-'}</div>
              <div className="text-xs text-slate-500">{swap.swap_time || swap.time || '-'}</div>
            </div>
          </div>
        </TableCell>
        <TableCell className="font-medium text-slate-800"><PlatePair oldPlate={swap.bike_plate_number || swap.motorcycle_plate} goldenPlate={swap.bike_golden_plate_number} compact /></TableCell>
        <TableCell className="text-slate-600">{swap.lessee_name || '-'}</TableCell>
        <TableCell>
          {swap.rider_name ? (
            <div>
              <div>{swap.rider_name}</div>
              <div className="text-xs text-slate-500">{swap.rider_emirates_id}</div>
            </div>
          ) : '-'}
        </TableCell>
        <TableCell className="text-slate-600">
          {swap.technician_name || '-'}
        </TableCell>
        <TableCell>{swap.odo_reading}</TableCell>
        <TableCell>
          <div className="text-xs space-y-1">
            {(swap.return_battery1_serial || swap.return_battery_1_serial) && (
              <div className="bg-red-100 px-2 py-1 rounded font-mono">{swap.return_battery1_serial || swap.return_battery_1_serial} ({(swap.return_battery1_soc || swap.return_battery_1_soc || 0)}%)</div>
            )}
            {(swap.return_battery2_serial || swap.return_battery_2_serial) && (
              <div className="bg-red-100 px-2 py-1 rounded font-mono">{swap.return_battery2_serial || swap.return_battery_2_serial} ({(swap.return_battery2_soc || swap.return_battery_2_soc || 0)}%)</div>
            )}
            {!(swap.return_battery1_serial || swap.return_battery_1_serial) && !(swap.return_battery2_serial || swap.return_battery_2_serial) && '-'}
          </div>
        </TableCell>
        <TableCell>
          <div className="text-xs space-y-1">
            <div className="bg-green-100 px-2 py-1 rounded font-mono">{swap.issued_battery1_serial || swap.issued_battery_1_serial || '-'} ({(swap.issued_battery1_soc || swap.issued_battery_1_soc || 0)}%)</div>
            <div className="bg-green-100 px-2 py-1 rounded font-mono">{swap.issued_battery2_serial || swap.issued_battery_2_serial || '-'} ({(swap.issued_battery2_soc || swap.issued_battery_2_soc || 0)}%)</div>
          </div>
        </TableCell>
        <TableCell>{swap.location || swap.location_name || '-'}</TableCell>
        {(canEdit || canDelete) && (
          <TableCell>
            <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
              {canEdit && (
                <Button size="sm" variant="ghost" onClick={() => handleEdit(swap)} className="hover:bg-green-100">
                  <Edit className="w-4 h-4" />
                </Button>
              )}
              {canDelete && (
                <Button size="sm" variant="ghost" onClick={() => handleDelete(swap.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </TableCell>
        )}
      </TableRow>
    ));
  };
  
  // Filter state
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    lessee: '',
    rider: '',
    bike: '',
    location: '',
    technician: ''
  });
  const getCurrentDateTime = () => {
    const now = new Date();
    const date = now.toISOString().split('T')[0];
    const time = now.toTimeString().split(' ')[0].substring(0, 5);
    return { date, time };
  };

  const [formData, setFormData] = useState({
    swap_date: getCurrentDateTime().date,
    swap_time: getCurrentDateTime().time,
    bike_id: '',
    bike_plate_number: '',
    rider_id: '',
    rider_name: '',
    rider_emirates_id: '',
    lessee_id: '',
    lessee_name: '',
    odo_reading: 0,
    return_battery1_serial: '',
    return_battery2_serial: '',
    return_battery1_soc: 0,
    return_battery2_soc: 0,
    issued_battery1_serial: '',
    issued_battery2_serial: '',
    issued_battery1_soc: 100,
    issued_battery2_soc: 100,
    location: '',
    technician_name: '',
    notes: ''
  });

  // Set default location when user data is available
  useEffect(() => {
    if (user && !editingId) {
      setFormData(prev => ({
        ...prev,
        location: prev.location || user.default_location || ''
      }));
    }
  }, [user, editingId]);

  useEffect(() => {
    fetchData();
  }, []);

  // Re-fetch from server (page 1) whenever filters change.
  // Debounced so typing/selecting in filters doesn't spam the API.
  // Skip the initial mount run because fetchData() above already covers it.
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const timer = setTimeout(() => {
      fetchData(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.fromDate, filters.toDate, filters.lessee, filters.rider, filters.bike, filters.location, filters.technician]);

  // Handle swap_id from URL parameter (for opening specific swap from alerts)
  useEffect(() => {
    const swapIdParam = searchParams.get('swap_id');
    if (swapIdParam && !loading) {
      // First check if swap is in the current list
      const swap = swaps.find(s => s.id === swapIdParam);
      if (swap) {
        setSelectedSwap(swap);
        setDetailDialogOpen(true);
        setSearchParams({});
      } else {
        // Fetch the specific swap from API
        const fetchSwapById = async () => {
          try {
            const response = await axios.get(`${API}/battery-swaps/by-id/${swapIdParam}`, {
              headers: getAuthHeader()
            });
            setSelectedSwap(response.data);
            setDetailDialogOpen(true);
            setSearchParams({});
          } catch (error) {
            console.error('Error fetching swap by ID:', error);
            toast.error('Could not find the specified battery swap');
            setSearchParams({});
          }
        };
        fetchSwapById();
      }
    }
  }, [swaps, searchParams, setSearchParams, loading]);

  // Apply sorting only — filtering is now server-side via fetchData query params
  const normalizedSwaps = swaps.map(swap => ({
    ...swap,
    date: swap.swap_date // Normalize date field for filtering
  }));

  const sortedSwaps = sortData(normalizedSwaps, sortConfig.field, sortConfig.direction);

  const handleSort = (field, direction) => {
    setSortConfig({ field, direction });
  };

  const fetchData = async (page = 1, filterOverride = null) => {
    try {
      setLoading(true);

      // Calculate skip value for pagination
      const skipValue = (page - 1) * ITEMS_PER_PAGE;

      // Build query params with current filters (server-side filtering).
      // The SearchableSelect components in SearchFilters store name/plate strings
      // directly as their `value` (not record IDs), so we forward them as-is.
      const f = filterOverride || filters;
      const swapParams = new URLSearchParams();
      swapParams.append('limit', String(ITEMS_PER_PAGE));
      swapParams.append('skip', String(skipValue));
      if (f.location) swapParams.append('location', f.location);
      if (f.technician) swapParams.append('technician', f.technician);
      if (f.lessee) swapParams.append('lessee', f.lessee);
      if (f.rider) swapParams.append('rider', f.rider);
      if (f.bike) swapParams.append('bike', f.bike);
      if (f.fromDate) swapParams.append('from_date', f.fromDate);
      if (f.toDate) swapParams.append('to_date', f.toDate);

      // Fetch all data with individual error handling to prevent one failure from breaking everything
      const results = await Promise.allSettled([
        axios.get(`${API}/battery-swaps?${swapParams.toString()}`, { headers: getAuthHeader() }),
        ...(page === 1 ? [
          axios.get(`${API}/motorcycles/dropdown`, { headers: getAuthHeader() }),
          axios.get(`${API}/riders/dropdown`, { headers: getAuthHeader() }),
          axios.get(`${API}/lessees`, { headers: getAuthHeader() }),
          axios.get(`${API}/batteries`, { headers: getAuthHeader() }),
          axios.get(`${API}/swap-locations`, { headers: getAuthHeader() }),
          axios.get(`${API}/users/names`, { headers: getAuthHeader() }),
          axios.get(`${API}/talent/employees?employee_type=staff`, { headers: getAuthHeader() }),
          axios.get(`${API}/battery-swaps/filter-options`, { headers: getAuthHeader() })
        ] : [])
      ]);
      
      // Process results - use empty array if request failed
      const swapsRes = results[0];
      
      // Handle paginated response format
      if (swapsRes.status === 'fulfilled') {
        const responseData = swapsRes.value.data;
        const swapsData = responseData.data || responseData; // Handle both paginated and legacy format
        const paginationData = responseData.pagination || { total: swapsData.length, skip: 0, limit: ITEMS_PER_PAGE, has_more: false };
        
        setSwaps(swapsData);
        setTotalRecords(paginationData.total);
        setTotalPages(Math.ceil(paginationData.total / ITEMS_PER_PAGE));
        setCurrentPage(page);
      } else {
        setSwaps([]);
      }
      
      // Only set other data on initial load (page 1)
      if (page === 1) {
        const [, bikesRes, ridersRes, lesseesRes, batteriesRes, locationsRes, userNamesRes, talentStaffRes, filterOptsRes] = results;
        
        setMotorcycles(bikesRes?.status === 'fulfilled' ? bikesRes.value.data : []);
        setRiders(ridersRes?.status === 'fulfilled' ? ridersRes.value.data : []);
        setLessees(lesseesRes?.status === 'fulfilled' ? lesseesRes.value.data : []);
        setBatteries(batteriesRes?.status === 'fulfilled' ? batteriesRes.value.data : []);
        setLocations(locationsRes?.status === 'fulfilled' 
          ? locationsRes.value.data.filter(loc => loc.is_active) 
          : []);
        setUsers(userNamesRes?.status === 'fulfilled' ? userNamesRes.value.data : []);
        if (filterOptsRes?.status === 'fulfilled') {
          setFilterOptions(filterOptsRes.value.data);
        }
        
        // Process Talent staff - API returns { employees: [...], summary: {...} }
        if (talentStaffRes?.status === 'fulfilled') {
          const employees = talentStaffRes.value.data?.employees || talentStaffRes.value.data || [];
          setTalentStaff(Array.isArray(employees) ? employees : []);
        } else {
          setTalentStaff([]);
        }
        
        // Log any failures for debugging
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            const endpoints = ['battery-swaps', 'motorcycles', 'riders', 'lessees', 'batteries', 'swap-locations', 'users/names', 'talent/employees'];
            console.error(`Failed to fetch ${endpoints[index]}:`, result.reason?.message || result.reason);
          }
        });
      }
    } catch (error) {
      console.error('Fetch data error:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Page change handler
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      fetchData(page);
      // Scroll to top of table
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 4) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 2);
      const end = Math.min(totalPages - 1, currentPage + 2);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 3) {
        pages.push('...');
      }
      
      // Always show last page
      pages.push(totalPages);
    }
    
    return pages;
  };

  const handleEmiratesIdLookup = async () => {
    if (!emiratesIdLookup) {
      toast.error('Please enter Emirates ID');
      return;
    }

    setLoading(true);
    try {
      // Normalize Emirates ID (remove dashes and spaces for comparison)
      const normalizeEmiratesId = (id) => id.replace(/[-\s]/g, '');
      const normalizedLookup = normalizeEmiratesId(emiratesIdLookup);

      // First, look up rider in riders database
      const rider = riders.find(r => normalizeEmiratesId(r.emirates_id) === normalizedLookup);
      
      if (!rider) {
        toast.error(`Rider not found with Emirates ID: ${emiratesIdLookup}`);
        setLoading(false);
        return;
      }

      // Get the lessee info for this rider
      const lessee = lessees.find(l => l.id === rider.lessee_id);

      // Find assigned motorcycle for this rider
      const assignedBike = motorcycles.find(m => 
        m.assigned_rider_id === rider.id || 
        m.rider_id === rider.id ||
        rider.assigned_bike_id === m.id
      );

      // Try to get previous swap data
      let previousSwapData = null;
      try {
        const swapResponse = await axios.get(
          `${API}/battery-swaps/last-by-emirates/${emiratesIdLookup}`,
          { headers: getAuthHeader() }
        );
        previousSwapData = swapResponse.data;
        setLastSwapInfo(previousSwapData);
      } catch (swapError) {
        // No previous swap found - that's okay
        setLastSwapInfo(null);
      }

      // Auto-fill form with rider and bike data
      setFormData(prev => ({
        ...prev,
        rider_id: rider.id,
        rider_name: rider.name,
        rider_emirates_id: emiratesIdLookup,
        lessee_id: rider.lessee_id || '',
        lessee_name: lessee?.name || lessee?.company_name || '',
        bike_id: assignedBike ? assignedBike.id : '',
        bike_plate_number: assignedBike ? assignedBike.plate_number : '',
        // Auto-fill location with user's default location
        location: prev.location || user?.default_location || '',
        // Keep current technician selection or empty
        technician_name: prev.technician_name || '',
        // Auto-fill return batteries with last issued batteries
        return_battery1_serial: previousSwapData?.last_issued_batteries?.[0] || '',
        return_battery2_serial: previousSwapData?.last_issued_batteries?.[1] || ''
      }));

      // Set previous ODO reading if available
      if (previousSwapData?.odo_reading) {
        setPreviousOdoReading(previousSwapData.odo_reading);
      }

      if (previousSwapData) {
        toast.success(`Rider found! Previous swap: ${previousSwapData.last_swap_date}. Return batteries auto-filled.`);
      } else {
        toast.success('Rider found! No previous swap record.');
      }
    } catch (error) {
      toast.error('Failed to fetch rider data');
    } finally {
      setLoading(false);
    }
  };

  const validateBatteries = () => {
    const batteries = [
      formData.return_battery1_serial,
      formData.return_battery2_serial,
      formData.issued_battery1_serial,
      formData.issued_battery2_serial
    ].filter(b => b);

    // Check all 4 batteries are unique
    const uniqueBatteries = new Set(batteries);
    if (uniqueBatteries.size !== batteries.length) {
      toast.error('All 4 battery serial numbers must be unique!');
      return false;
    }

    // Validate serial number format
    const validFormats = /^(EMBY\d{7}|EM\d{8}|EMBY\d{6})$/;
    for (const serial of batteries) {
      if (!validFormats.test(serial)) {
        toast.error(`Invalid battery serial format: ${serial}`);
        return false;
      }
    }

    return true;
  };

  const detectMismatch = () => {
    if (lastSwapInfo) {
      const expectedBatteries = [lastSwapInfo.last_issued_batteries[0], lastSwapInfo.last_issued_batteries[1]];
      const actualBatteries = [formData.return_battery1_serial, formData.return_battery2_serial];

      const mismatch = expectedBatteries.some((expected, index) => expected !== actualBatteries[index]);

      if (mismatch) {
        return {
          expected: expectedBatteries,
          actual: actualBatteries
        };
      }
    }
    return null;
  };

  const fetchMismatchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/battery-mismatch-alerts?status=pending`, { headers: getAuthHeader() });
      setMismatchAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  };

  const handleViewAlert = (alert) => {
    setSelectedAlert(alert);
    
    // Initialize battery updates for expected batteries
    const initialUpdates = alert.expected_batteries?.map(battery => ({
      battery_serial: battery,
      status: '',
      location: '',
      rider_id: '',
      notes: ''
    })) || [];
    setBatteryUpdates(initialUpdates);
    
    // Fetch location options
    fetchBatteryLocationOptions();
    setViewAlertDialogOpen(true);
  };

  const fetchBatteryLocationOptions = async () => {
    try {
      const response = await axios.get(`${API}/battery-locations`, { headers: getAuthHeader() });
      setBatteryLocationOptions(response.data);
    } catch (error) {
      console.error('Error fetching battery location options:', error);
    }
  };

  const updateBatteryStatus = (index, field, value) => {
    const updated = [...batteryUpdates];
    updated[index] = { ...updated[index], [field]: value };
    
    // Clear fields based on status selection
    if (field === 'status') {
      if (value === 'with-rider') {
        // Clear location and notes, keep rider_id field available
        updated[index].location = '';
        updated[index].notes = '';
      } else if (value === 'at-station') {
        // Clear rider_id and notes, keep location field available
        updated[index].rider_id = '';
        updated[index].notes = '';
      } else if (value === 'lost-missing') {
        // Clear all other fields - simple status
        updated[index].rider_id = '';
        updated[index].location = 'Lost/Missing';
        updated[index].notes = '';
      } else if (value === 'other') {
        // Clear rider_id and location, keep notes field available
        updated[index].rider_id = '';
        updated[index].location = '';
        // Don't clear notes - let admin describe the situation
      } else {
        // Clear all fields for any other case
        updated[index].rider_id = '';
        updated[index].location = '';
        updated[index].notes = '';
      }
    }
    
    setBatteryUpdates(updated);
  };

  const handleResolveAlert = async (alertId, notes) => {
    try {
      // Validate that all battery updates have status selected and required fields filled
      const incompleteUpdates = batteryUpdates.filter(update => {
        if (!update.status) return true; // Status not selected
        
        // Additional validation based on status
        if (update.status === 'with-rider' && !update.rider_id) return true;
        if (update.status === 'at-station' && !update.location) return true;
        if (update.status === 'other' && !update.notes?.trim()) return true;
        
        return false;
      });
      
      if (incompleteUpdates.length > 0) {
        toast.error('Please complete all required fields for each expected battery');
        return;
      }

      await axios.put(
        `${API}/battery-mismatch-alerts/${alertId}/resolve`,
        {
          resolution_notes: notes,
          battery_updates: batteryUpdates
        },
        { headers: getAuthHeader() }
      );
      
      toast.success('Alert resolved and battery statuses updated successfully');
      setViewAlertDialogOpen(false);
      setResolutionNotes('');
      setBatteryUpdates([]);
      fetchMismatchAlerts(); // Refresh the alerts list
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/battery-mismatch-alerts/${alertId}`, { headers: getAuthHeader() });
      toast.success('Alert deleted successfully');
      fetchMismatchAlerts();
      setViewAlertDialogOpen(false);
      setResolutionNotes('');
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };

  const handlePhotoUpload = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Photo size must be less than 10MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const newPhotos = [...mismatchForm.photos];
        newPhotos[index] = {
          file: file,
          preview: reader.result
        };
        setMismatchForm({ ...mismatchForm, photos: newPhotos });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMismatchSubmit = async () => {
    if (!mismatchForm.description) {
      toast.error('Please provide a description of the mismatch');
      return;
    }

    if (mismatchForm.photos.filter(p => p).length < 5) {
      toast.error('Please upload all 5 required photos');
      return;
    }

    setLoading(true);
    try {
      // First, save the swap
      await saveSwap();

      // Then create mismatch alert
      const alertData = {
        swap_id: formData.bike_id, // Temporary, will be updated in backend
        emirates_id: formData.rider_emirates_id,
        rider_name: formData.rider_name,
        bike_number: formData.bike_plate_number,
        expected_batteries: mismatchData.expected,
        actual_batteries: mismatchData.actual,
        description: mismatchForm.description
      };

      const formDataToSend = new FormData();
      formDataToSend.append('data', JSON.stringify(alertData));
      
      mismatchForm.photos.forEach((photo, index) => {
        if (photo && photo.file) {
          formDataToSend.append(`photos`, photo.file);
        }
      });

      await axios.post(`${API}/battery-mismatch-alerts`, formDataToSend, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('⚠️ Mismatch alert created! Swap recorded.');
      setMismatchDialogOpen(false);
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to create mismatch alert');
    } finally {
      setLoading(false);
    }
  };

  const handleBikeSelect = async (bikeId) => {
    const bike = motorcycles.find(b => b.id === bikeId);
    if (bike) {
      let bikeData = {
        bike_id: bikeId,
        bike_plate_number: bike.plate_number,
        bike_golden_plate_number: bike.golden_plate_number || '',
        // Auto-fill location with user's default location
        location: user?.default_location || formData.location || '',
        // Keep current technician selection
        technician_name: formData.technician_name || ''
      };
      
      // If motorcycle has assigned rider, auto-fill rider information
      if (bike.assigned_rider_id || bike.rider_id) {
        const assignedRiderId = bike.assigned_rider_id || bike.rider_id;
        const assignedRider = riders.find(r => r.id === assignedRiderId);
        
        if (assignedRider) {
          bikeData.rider_id = assignedRider.id;
          bikeData.rider_name = assignedRider.name;
          bikeData.rider_emirates_id = assignedRider.emirates_id;
          toast.success(`Auto-filled rider: ${assignedRider.name}`);
        }
      }
      
      // Fetch previous swap data for this motorcycle
      try {
        const response = await axios.get(`${API}/battery-swaps/last-by-bike/${bikeId}`, {
          headers: getAuthHeader()
        });
        
        // Set previous ODO reading
        setPreviousOdoReading(response.data.last_odo_reading || 0);
        setOdoWarning(null);
        setLastSwapInfo(response.data);
        
        // Auto-fill return batteries directly from this response
        if (response.data?.last_issued_batteries) {
          const bat1 = response.data.last_issued_batteries[0];
          const bat2 = response.data.last_issued_batteries[1];
          if (bat1) bikeData.return_battery1_serial = bat1;
          if (bat2) bikeData.return_battery2_serial = bat2;
          toast.info(`Return batteries auto-filled from last swap`);
        }
        
        // If we don't have rider info from bike assignment, use from last swap
        if (!bikeData.rider_emirates_id && response.data.rider_emirates_id) {
          bikeData.rider_emirates_id = response.data.rider_emirates_id;
          bikeData.rider_name = response.data.rider_name;
        }
      } catch (error) {
        // No previous swap found - that's okay
        setPreviousOdoReading(null);
        setOdoWarning(null);
        setLastSwapInfo(null);
      }
      
      setFormData(prev => ({
        ...prev,
        ...bikeData
      }));
    }
  };

  // Validate ODO reading and show warning
  const validateOdoReading = (currentOdo) => {
    if (previousOdoReading === null) {
      setOdoWarning(null);
      return;
    }
    
    const prevOdo = previousOdoReading || 0;
    const currOdo = parseFloat(currentOdo) || 0;
    
    if (currOdo <= 0) {
      setOdoWarning({
        type: 'error',
        message: 'ODO reading value is zero or invalid'
      });
    } else if (currOdo < prevOdo) {
      setOdoWarning({
        type: 'error',
        message: `ODO reading value (${currOdo} km) is less than previous reading (${prevOdo} km)`
      });
    } else if ((currOdo - prevOdo) > 250) {
      setOdoWarning({
        type: 'warning',
        message: `ODO reading exceeds 250 km from previous reading (${prevOdo} km → ${currOdo} km = +${(currOdo - prevOdo).toFixed(1)} km)`
      });
    } else {
      setOdoWarning(null);
    }
  };

  const handleOdoChange = (value) => {
    const odoValue = parseFloat(value) || 0;
    setFormData({ ...formData, odo_reading: odoValue });
    validateOdoReading(odoValue);
  };

  const handleRiderSelect = async (riderId) => {
    const rider = riders.find(r => r.id === riderId);
    if (rider) {
      // Get the lessee info for this rider
      const lessee = lessees.find(l => l.id === rider.lessee_id);
      
      let riderData = {
        rider_id: riderId,
        rider_name: rider.name,
        rider_emirates_id: rider.emirates_id,
        lessee_id: rider.lessee_id || '',
        lessee_name: lessee?.name || lessee?.company_name || '',
        // Auto-fill location with user's default location
        location: formData.location || user?.default_location || '',
        // Keep current technician selection
        technician_name: formData.technician_name || ''
      };
      
      // Try to get previous swap data to auto-fill return batteries
      try {
        const swapResponse = await axios.get(
          `${API}/battery-swaps/last-by-emirates/${rider.emirates_id}`,
          { headers: getAuthHeader() }
        );
        setLastSwapInfo(swapResponse.data);
        setPreviousOdoReading(swapResponse.data?.odo_reading || null);
        
        // Auto-fill return batteries with last issued batteries
        if (swapResponse.data?.last_issued_batteries) {
          riderData.return_battery1_serial = swapResponse.data.last_issued_batteries[0] || '';
          riderData.return_battery2_serial = swapResponse.data.last_issued_batteries[1] || '';
        }
        
        // Auto-fill bike if available
        if (swapResponse.data?.bike_id) {
          riderData.bike_id = swapResponse.data.bike_id;
          riderData.bike_plate_number = swapResponse.data.bike_number;
        }
        
        toast.success(`Previous swap found! Return batteries auto-filled.`);
      } catch (error) {
        // No previous swap found
        setLastSwapInfo(null);
        setPreviousOdoReading(null);
      }
      
      setFormData({
        ...formData,
        ...riderData
      });
    }
  };

  const handleReturnBattery1Select = (serialNumber) => {
    const battery = batteries.find(b => b.serial_number === serialNumber);
    setFormData({
      ...formData,
      return_battery1_serial: serialNumber,
      return_battery1_soc: battery?.health_percentage || 0
    });
  };

  const handleReturnBattery2Select = (serialNumber) => {
    const battery = batteries.find(b => b.serial_number === serialNumber);
    setFormData({
      ...formData,
      return_battery2_serial: serialNumber,
      return_battery2_soc: battery?.health_percentage || 0
    });
  };

  const handleIssuedBattery1Select = (serialNumber) => {
    const battery = batteries.find(b => b.serial_number === serialNumber);
    setFormData({
      ...formData,
      issued_battery1_serial: serialNumber,
      issued_battery1_soc: battery?.health_percentage || 100
    });
  };

  const handleIssuedBattery2Select = (serialNumber) => {
    const battery = batteries.find(b => b.serial_number === serialNumber);
    setFormData({
      ...formData,
      issued_battery2_serial: serialNumber,
      issued_battery2_soc: battery?.health_percentage || 100
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate batteries
    if (!validateBatteries()) {
      return;
    }

    // Detect mismatch
    const mismatch = detectMismatch();
    if (mismatch && !editingId) {
      setMismatchData(mismatch);
      setMismatchDialogOpen(true);
      return;
    }

    await saveSwap();
  };

  const saveSwap = async (opts = {}) => {
    try {
      const payload = {
        ...formData,
        force_save: !!opts.force_save,
        force_save_reason: opts.force_save_reason || null,
        odo_reset_confirmed: !!opts.odo_reset_confirmed,
      };
      if (editingId) {
        await axios.put(`${API}/battery-swaps/${editingId}`, payload, { headers: getAuthHeader() });
        toast.success('Battery swap updated successfully');
      } else {
        await axios.post(`${API}/battery-swaps`, payload, { headers: getAuthHeader() });
        toast.success('Battery swap recorded successfully');
      }
      setDialogOpen(false);
      setAnomalyBlock(null);
      setOverrideReason('');
      resetForm();
      fetchData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const isBlock = detail && typeof detail === 'object'
        && ['missing_reading', 'typo_spike', 'odo_reset_confirm'].includes(detail.code);
      if (isBlock) {
        setAnomalyBlock(detail);
      } else {
        toast.error(formatErrorMessage(error, 'Failed to save battery swap'));
      }
    }
  };

  const handleEdit = (swap) => {
    setEditingId(swap.id);
    setFormData({
      swap_date: swap.swap_date || swap.date || '',
      swap_time: swap.swap_time || swap.time || '',
      bike_id: swap.bike_id || swap.motorcycle_id || '',
      bike_plate_number: swap.bike_plate_number || swap.motorcycle_plate || '',
      rider_id: swap.rider_id || '',
      rider_name: swap.rider_name || '',
      rider_emirates_id: swap.rider_emirates_id || '',
      lessee_id: swap.lessee_id || '',
      lessee_name: swap.lessee_name || '',
      odo_reading: swap.odo_reading,
      return_battery1_serial: swap.return_battery1_serial || swap.return_battery_1_serial || '',
      return_battery2_serial: swap.return_battery2_serial || swap.return_battery_2_serial || '',
      return_battery1_soc: swap.return_battery1_soc || swap.return_battery_1_soc || 0,
      return_battery2_soc: swap.return_battery2_soc || swap.return_battery_2_soc || 0,
      issued_battery1_serial: swap.issued_battery1_serial || swap.issued_battery_1_serial || '',
      issued_battery2_serial: swap.issued_battery2_serial || swap.issued_battery_2_serial || '',
      issued_battery1_soc: swap.issued_battery1_soc || swap.issued_battery_1_soc || 0,
      issued_battery2_soc: swap.issued_battery2_soc || swap.issued_battery_2_soc || 0,
      location: swap.location || swap.location_name || '',
      technician_name: swap.technician_name || '',
      notes: swap.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this battery swap record?')) return;
    
    try {
      await axios.delete(`${API}/battery-swaps/${id}`, { headers: getAuthHeader() });
      toast.success('Battery swap deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete battery swap');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setEmiratesIdLookup('');
    setLastSwapInfo(null);
    setMismatchData(null);
    setMismatchForm({ description: '', photos: [] });
    setPreviousOdoReading(null);
    setOdoWarning(null);
    const { date, time } = getCurrentDateTime();
    // Use user's default location if set
    const defaultLocation = user?.default_location || '';
    setFormData({
      swap_date: date,
      swap_time: time,
      bike_id: '',
      bike_plate_number: '',
      rider_id: '',
      rider_name: '',
      rider_emirates_id: '',
      lessee_id: '',
      lessee_name: '',
      odo_reading: 0,
      return_battery1_serial: '',
      return_battery2_serial: '',
      return_battery1_soc: 0,
      return_battery2_soc: 0,
      issued_battery1_serial: '',
      issued_battery2_serial: '',
      issued_battery1_soc: 100,
      issued_battery2_soc: 100,
      location: defaultLocation,
      technician_name: '',
      notes: ''
    });
  };

  const handleViewDetails = (swap) => {
    setSelectedSwap(swap);
    setDetailDialogOpen(true);
  };

  const handleDownload = async (format, allRecords) => {
    try {
      setExporting(true);
      
      // Build query params for the export endpoint
      const params = new URLSearchParams();
      if (!allRecords) {
        // Apply current filters (values are already plates/names, not record IDs)
        if (filters.location) params.append('location', filters.location);
        if (filters.technician) params.append('technician', filters.technician);
        if (filters.lessee) params.append('lessee', filters.lessee);
        if (filters.rider) params.append('rider', filters.rider);
        if (filters.bike) params.append('bike', filters.bike);
        if (filters.fromDate) params.append('from_date', filters.fromDate);
        if (filters.toDate) params.append('to_date', filters.toDate);
      }

      // Fetch all matching records from backend (no pagination)
      const res = await axios.get(`${API}/battery-swaps/export?${params.toString()}`, { headers: getAuthHeader() });
      const dataToExport = res.data;
      
      if (!dataToExport || dataToExport.length === 0) {
        toast.error('No records to download');
        return;
      }

      const rows = dataToExport.map(swap => ({
        'Date': swap.swap_date || swap.date || '-',
        'Time': swap.swap_time || swap.time || '-',
        'Bike Plate': swap.bike_plate_number || swap.motorcycle_plate || '-',
        'Rider Name': swap.rider_name || '-',
        'Lessee': swap.lessee_name || getLesseeName(swap.lessee_id) || '-',
        'Location': swap.location_name || swap.location || '-',
        'Technician': swap.technician_name || swap.technician || '-',
        'ODO Reading': swap.odo_reading || '-',
        'Return Battery 1 Serial': swap.return_battery_1_serial || swap.return_battery1_serial || '-',
        'Return Battery 1 SOC %': swap.return_battery_1_soc ?? swap.return_battery1_soc ?? '-',
        'Return Battery 2 Serial': swap.return_battery_2_serial || swap.return_battery2_serial || '-',
        'Return Battery 2 SOC %': swap.return_battery_2_soc ?? swap.return_battery2_soc ?? '-',
        'Issued Battery 1 Serial': swap.issued_battery_1_serial || swap.issued_battery1_serial || '-',
        'Issued Battery 1 SOC %': swap.issued_battery_1_soc ?? swap.issued_battery1_soc ?? '-',
        'Issued Battery 2 Serial': swap.issued_battery_2_serial || swap.issued_battery2_serial || '-',
        'Issued Battery 2 SOC %': swap.issued_battery_2_soc ?? swap.issued_battery2_soc ?? '-',
        'Notes': swap.notes || ''
      }));

      const label = allRecords ? 'All' : 'Filtered';
      const dateStr = new Date().toISOString().split('T')[0];

      if (format === 'excel') {
        const stats = [
          { label: 'Report Type', value: `Battery Swaps - ${label} Records` },
          { label: 'Generated', value: new Date().toLocaleString() },
          { label: 'Total Records', value: rows.length }
        ];
        await exportToExcel(rows, `Battery_Swaps_${label}_${dateStr}.xlsx`, 'Battery Swaps', stats);
        toast.success(`Excel downloaded (${rows.length} records)`);
      } else {
        const columns = ['Date', 'Time', 'Bike Plate', 'Rider Name', 'Location', 'Technician', 'ODO', 'Return Bat1', 'Return Bat2', 'Issued Bat1', 'Issued Bat2'];
        const pdfRows = dataToExport.map(swap => [
          swap.swap_date || swap.date || '-',
          swap.swap_time || swap.time || '-',
          swap.bike_plate_number || swap.motorcycle_plate || '-',
          swap.rider_name || '-',
          swap.location_name || swap.location || '-',
          swap.technician_name || swap.technician || '-',
          swap.odo_reading || '-',
          `${swap.return_battery_1_serial || swap.return_battery1_serial || '-'}\n(${swap.return_battery_1_soc ?? swap.return_battery1_soc ?? '-'}%)`,
          `${swap.return_battery_2_serial || swap.return_battery2_serial || '-'}\n(${swap.return_battery_2_soc ?? swap.return_battery2_soc ?? '-'}%)`,
          `${swap.issued_battery_1_serial || swap.issued_battery1_serial || '-'}\n(${swap.issued_battery_1_soc ?? swap.issued_battery1_soc ?? '-'}%)`,
          `${swap.issued_battery_2_serial || swap.issued_battery2_serial || '-'}\n(${swap.issued_battery_2_soc ?? swap.issued_battery2_soc ?? '-'}%)`
        ]);
        await exportToPDF(
          columns,
          pdfRows,
          `Battery_Swaps_${label}_${dateStr}.pdf`,
          `Battery Swaps Report - ${label} Records`
        );
        toast.success(`PDF downloaded (${rows.length} records)`);
      }
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download. Please try again.');
    } finally {
      setExporting(false);
    }
  };


  const handleAddLocation = async () => {
    if (!newLocation.trim()) {
      toast.error('Please enter a location name');
      return;
    }

    try {
      await axios.post(`${API}/swap-locations`, 
        { name: newLocation.trim(), is_active: true }, 
        { headers: getAuthHeader() }
      );
      toast.success('Location added successfully');
      setNewLocation('');
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to add location'));
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;

    try {
      await axios.delete(`${API}/swap-locations/${locationId}`, { headers: getAuthHeader() });
      toast.success('Location deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to delete location'));
    }
  };

  // Use proper permission-based access control
  const canManage = canCreate || canEdit || canDelete;
  const isAdmin = permissions.isAdmin;

  if (loading) return <div className="flex items-center justify-center h-96">Loading...</div>;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="battery-swaps-page">
      {/* Responsive Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800">{t('batterySwaps.title')}</h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">{t('batterySwaps.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canViewAlerts && (
            <Button 
              variant="outline" 
              size={isMobile ? "sm" : "default"}
              className="border-orange-600 text-orange-600 hover:bg-orange-50"
              onClick={() => {
                fetchMismatchAlerts();
                setAlertsDialogOpen(true);
              }}
            >
              <AlertCircle className="w-4 h-4 mr-2" />
              {!isMobile && 'View'} Alerts
              {mismatchAlerts.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {mismatchAlerts.length}
                </span>
              )}
            </Button>
          )}
          {canViewSwapLocations && (
            <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size={isMobile ? "sm" : "default"} className="border-lime-600 text-lime-600 hover:bg-lime-50">
                  <Settings className="w-4 h-4 mr-2" />
                  {!isMobile && t('batterySwaps.manageLocations')}
                  {isMobile && 'Locations'}
                </Button>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Manage Swap Locations</DialogTitle>
                <DialogDescription>
                  Add or remove battery swapping locations
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {canCreateSwapLocations && (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter new location name"
                      value={newLocation}
                      onChange={(e) => setNewLocation(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddLocation()}
                    />
                    <Button onClick={handleAddLocation} className="bg-gradient-to-r from-lime-600 to-green-600">
                      <Plus className="w-4 h-4 mr-2" />
                      Add
                    </Button>
                  </div>
                )}
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Location Name</TableHead>
                          <TableHead className="w-20">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {locations.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={2} className="text-center text-slate-500 py-4">
                              No locations found
                            </TableCell>
                          </TableRow>
                        ) : (
                          locations.map(location => (
                            <TableRow key={location.id}>
                              <TableCell className="font-medium">{location.name}</TableCell>
                              <TableCell>
                                {canDeleteSwapLocations && (
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    onClick={() => handleDeleteLocation(location.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}

      {/* View Alert Details Dialog */}
      <Dialog open={viewAlertDialogOpen} onOpenChange={setViewAlertDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-4xl'} max-h-[90vh] flex flex-col`}>
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              Battery Mismatch Alert Details
            </DialogTitle>
            <DialogDescription>
              Review the mismatch details and take corrective action
            </DialogDescription>
          </DialogHeader>
          
          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto pr-2 space-y-4">
            {selectedAlert && (
              <>
                {/* Alert Summary - Compact */}
                <Card className="border-orange-300 bg-orange-50">
                  <CardContent className="p-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div><strong>Rider:</strong> {selectedAlert.rider_name}</div>
                      <div><strong>Bike:</strong> {selectedAlert.bike_number}</div>
                      <div><strong>Emirates ID:</strong> {selectedAlert.emirates_id}</div>
                      <div><strong>Date:</strong> {formatDateDisplay(selectedAlert.created_at)}</div>
                    </div>
                  </CardContent>
                </Card>

                {/* Battery Comparison - Compact */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Battery Mismatch Details</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="grid md:grid-cols-2 gap-4 mb-3">
                      <div>
                        <h4 className="font-semibold text-green-700 text-sm mb-2">Expected:</h4>
                        <div className="space-y-1">
                          {selectedAlert.expected_batteries?.map((battery, i) => (
                            <div key={i} className="p-1.5 bg-green-50 border border-green-200 rounded font-mono text-xs">
                              {battery}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-red-700 text-sm mb-2">Actually Returned:</h4>
                        <div className="space-y-1">
                          {selectedAlert.actual_batteries?.map((battery, i) => (
                            <div key={i} className="p-1.5 bg-red-50 border border-red-200 rounded font-mono text-xs text-red-700">
                              {battery}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                      <strong>Resolution:</strong> Actual batteries confirmed with {selectedAlert.rider_name}. Track expected batteries below.
                    </div>
                  </CardContent>
                </Card>

                {/* Battery Status Tracking - Compact */}
                <Card className="border-yellow-300 bg-yellow-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-yellow-800 flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      Track Expected Batteries
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-3">
                    {selectedAlert.expected_batteries?.map((battery, i) => (
                      <Card key={i} className="border-gray-200 bg-white">
                        <CardContent className="p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="font-mono text-sm font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded">
                              {battery}
                            </div>
                            <span className="text-xs text-gray-500">Where is this battery?</span>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {/* Status Selection */}
                            <div>
                              <label className="block text-xs font-semibold mb-1">
                                Status <span className="text-red-500">*</span>
                              </label>
                              <select
                                value={batteryUpdates[i]?.status || ''}
                                onChange={(e) => updateBatteryStatus(i, 'status', e.target.value)}
                                className="w-full p-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                required
                              >
                                <option value="">Select...</option>
                                {batteryLocationOptions.statuses.map(status => (
                                  <option key={status.value} value={status.value}>
                                    {status.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* Conditional fields */}
                            {batteryUpdates[i]?.status === 'with-rider' && (
                              <div className="md:col-span-2">
                                <label className="block text-xs font-semibold mb-1">
                                  Rider <span className="text-red-500">*</span>
                                </label>
                                <select
                                  value={batteryUpdates[i]?.rider_id || ''}
                                  onChange={(e) => {
                                    updateBatteryStatus(i, 'rider_id', e.target.value);
                                    const rider = batteryLocationOptions.riders.find(r => r.id === e.target.value);
                                    updateBatteryStatus(i, 'location', rider ? `With ${rider.name}` : '');
                                  }}
                                  className="w-full p-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                  required
                                >
                                  <option value="">Select rider...</option>
                                  {batteryLocationOptions.riders.map(rider => (
                                    <option key={rider.id} value={rider.id}>
                                      {rider.name} ({rider.emirates_id})
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}

                            {batteryUpdates[i]?.status === 'at-station' && (
                              <div className="md:col-span-2">
                                <label className="block text-xs font-semibold mb-1">
                                  Battery Station <span className="text-red-500">*</span>
                                </label>
                                <select
                                  value={batteryUpdates[i]?.location || ''}
                                  onChange={(e) => updateBatteryStatus(i, 'location', e.target.value)}
                                  className="w-full p-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                  required
                                >
                                  <option value="">Select station...</option>
                                  {batteryLocationOptions.locations.map(location => (
                                    <option key={location} value={location}>
                                      {location}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}

                            {batteryUpdates[i]?.status === 'other' && (
                              <div className="md:col-span-2">
                                <label className="block text-xs font-semibold mb-1">
                                  Describe Status <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  value={batteryUpdates[i]?.notes || ''}
                                  onChange={(e) => {
                                    updateBatteryStatus(i, 'notes', e.target.value);
                                    updateBatteryStatus(i, 'location', `Other: ${e.target.value}`);
                                  }}
                                  placeholder="e.g., At repair shop, With mechanic, In transit..."
                                  className="w-full p-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                  required
                                />
                                <div className="text-xs text-gray-500 mt-1">
                                  This will remain as "Other" status until you change it to With Rider, At Station, or Lost/Missing
                                </div>
                              </div>
                            )}

                            {batteryUpdates[i]?.status === 'lost-missing' && (
                              <div className="md:col-span-2">
                                <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                                  <strong>Status:</strong> Battery marked as Lost/Missing
                                  <br />
                                  <span className="text-xs text-gray-600">No additional information required</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </CardContent>
                </Card>

                {/* Description - Compact */}
                {selectedAlert.description && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Description</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-xs">{selectedAlert.description}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Photos - Compact */}
                {selectedAlert.photos && selectedAlert.photos.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Photos</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="grid grid-cols-6 gap-2">
                        {selectedAlert.photos.map((photo, i) => (
                          <img
                            key={i}
                            src={photo}
                            alt={`Evidence ${i + 1}`}
                            className="w-full aspect-square object-cover rounded cursor-pointer hover:opacity-75 border"
                            onClick={() => setSelectedImage(photo)}
                          />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>

          {/* Fixed Bottom Section - Always Visible */}
          <div className="flex-shrink-0 border-t pt-4 mt-4">
            {/* Resolution Notes */}
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">
                Resolution Notes <span className="text-red-500">*</span>
              </label>
              <Textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Describe the corrective action taken..."
                rows={2}
                className="w-full text-sm"
              />
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={() => handleResolveAlert(selectedAlert?.id, resolutionNotes)}
                disabled={!resolutionNotes.trim()}
                className="bg-green-600 hover:bg-green-700"
                size="sm"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Mark as Resolved
              </Button>
              
              <Button
                variant="outline"
                onClick={() => {
                  setViewAlertDialogOpen(false);
                  setResolutionNotes('');
                  setBatteryUpdates([]);
                }}
                size="sm"
              >
                Cancel
              </Button>
              
              <Button
                variant="destructive"
                onClick={() => {
                  if (confirm('Are you sure you want to delete this alert?')) {
                    handleDeleteAlert(selectedAlert?.id);
                  }
                }}
                className="ml-auto"
                size="sm"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Alert
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
          {canCreate && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-lime-600 to-green-600" data-testid="add-swap-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Record Battery Swap
                </Button>
              </DialogTrigger>
            <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-4xl'} max-h-[90vh] overflow-y-auto`}>
              <DialogHeader>
                <DialogTitle>{editingId ? 'Edit' : 'Record'} Battery Swap</DialogTitle>
                <DialogDescription>
                  Record battery swap details including return and issued batteries
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Step 1: Emirates ID Lookup */}
                {!editingId && (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Step 1: Emirates ID Lookup</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <Label>Emirates ID *</Label>
                          <Input
                            value={emiratesIdLookup}
                            onChange={(e) => setEmiratesIdLookup(e.target.value)}
                            placeholder="784XXXXXXXXXXXX"
                            maxLength={18}
                          />
                          <p className="text-xs text-slate-500 mt-1">15 digits without dashes</p>
                        </div>
                        <div className="flex items-end">
                          <Button 
                            type="button" 
                            onClick={handleEmiratesIdLookup}
                            disabled={loading || !emiratesIdLookup}
                          >
                            <Search className="w-4 h-4 mr-2" />
                            Lookup
                          </Button>
                        </div>
                      </div>

                      {lastSwapInfo ? (
                        <Alert className="bg-green-50 border-green-200">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <AlertDescription>
                            <div className="font-semibold text-green-800">Previous Swap Found:</div>
                            <div className="text-sm mt-2 space-y-1">
                              <div>Rider: {lastSwapInfo.rider_name}</div>
                              <div>Bike: {lastSwapInfo.bike_number || lastSwapInfo.bike_plate_number}</div>
                              <div>Last Swap: {formatDateDisplay(lastSwapInfo.last_swap_date)} at {lastSwapInfo.last_swap_time}</div>
                              <div className="font-semibold mt-2">Expected Return Batteries:</div>
                              <div>• {lastSwapInfo.last_issued_batteries?.[0] || 'N/A'}</div>
                              <div>• {lastSwapInfo.last_issued_batteries?.[1] || 'N/A'}</div>
                            </div>
                          </AlertDescription>
                        </Alert>
                      ) : (formData.bike_id || formData.rider_id) && (
                        <Alert className="bg-yellow-50 border-yellow-200">
                          <AlertCircle className="h-4 w-4 text-yellow-600" />
                          <AlertDescription>
                            <div className="font-semibold text-yellow-800">Previous swap not found</div>
                            <div className="text-sm text-yellow-700 mt-1">
                              No previous battery swap history found for this motorcycle/rider.
                            </div>
                          </AlertDescription>
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Swap Date *</Label>
                    <Input
                      type="date"
                      value={formData.swap_date}
                      onChange={(e) => setFormData({ ...formData, swap_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Swap Time *</Label>
                    <Input
                      type="time"
                      value={formData.swap_time}
                      onChange={(e) => setFormData({ ...formData, swap_time: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2 col-span-2">
                    <Label>Select Motorcycle *</Label>
                    <SearchableSelect
                      options={motorcycles.map(bike => ({
                        value: bike.id,
                        label: bike.golden_plate_number ? `${bike.plate_number} / ${bike.golden_plate_number}` : bike.plate_number,
                        searchText: `${bike.plate_number} ${bike.golden_plate_number || ''} ${bike.model_name} ${bike.chassis_number || ''}`,
                        metadata: {
                          'Model': bike.model_name,
                          'Chassis': bike.chassis_number,
                          'City': bike.city || '-',
                          'Status': bike.status
                        }
                      }))}
                      value={formData.bike_id}
                      onValueChange={handleBikeSelect}
                      placeholder="Select motorcycle"
                      emptyMessage="No motorcycles found"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Select Rider *</Label>
                    <SearchableSelect
                      options={riders.map(rider => ({
                        value: rider.id,
                        label: rider.name,
                        searchText: `${rider.name} ${rider.emirates_id} ${rider.mobile_uae || ''}`,
                        metadata: {
                          'Emirates ID': rider.emirates_id,
                          'Mobile': rider.mobile_uae,
                          'Platform': rider.platform
                        }
                      }))}
                      value={formData.rider_id}
                      onValueChange={handleRiderSelect}
                      placeholder="Select rider"
                      emptyMessage="No riders found"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Rider Emirates ID</Label>
                    <Input value={formData.rider_emirates_id} disabled />
                  </div>

                  <div className="space-y-2">
                    <Label>ODO Reading (km) *</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.odo_reading}
                      onChange={(e) => handleOdoChange(e.target.value)}
                      required
                      className={odoWarning ? (odoWarning.type === 'error' ? 'border-red-500' : 'border-orange-500') : ''}
                    />
                    {previousOdoReading !== null && previousOdoReading > 0 && (
                      <p className="text-xs text-pink-600 font-medium">
                        📍 Previous ODO reading: {previousOdoReading} km
                      </p>
                    )}
                    {odoWarning && (
                      <Alert className={`py-2 ${odoWarning.type === 'error' ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'}`}>
                        <AlertCircle className={`h-4 w-4 ${odoWarning.type === 'error' ? 'text-red-600' : 'text-orange-600'}`} />
                        <AlertDescription className={`text-xs ${odoWarning.type === 'error' ? 'text-red-700' : 'text-orange-700'}`}>
                          {odoWarning.message}
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Location *</Label>
                    <Select value={formData.location} onValueChange={(value) => setFormData({ ...formData, location: value })} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map(location => (
                          <SelectItem key={location.id} value={location.name}>
                            {location.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Technician Name *</Label>
                    <SearchableSelect
                      options={talentStaff
                        .filter(staff => {
                          const designation = (staff.designation || '').toLowerCase();
                          return designation.includes('bike technician') || designation.includes('technician');
                        })
                        .map((staff) => ({
                          value: staff.name,
                          label: staff.name,
                          searchText: `${staff.name} ${staff.designation || ''} ${staff.employee_unique_id || ''}`,
                          metadata: {
                            'ID': staff.employee_unique_id || 'N/A',
                            'Designation': staff.designation || 'Staff'
                          }
                        }))}
                      value={formData.technician_name}
                      onValueChange={(value) => setFormData({ ...formData, technician_name: value })}
                      placeholder="Select technician"
                      emptyMessage="No technicians found. Add staff with 'Bike technician' designation in Talent Management."
                      data-testid="swap-technician"
                    />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold mb-3 text-slate-700">Return Batteries</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Return Battery 1 Serial No.</Label>
                      <SearchableSelect
                        value={formData.return_battery1_serial}
                        onValueChange={handleReturnBattery1Select}
                        options={batteries.map(b => ({ value: b.serial_number, label: b.serial_number }))}
                        placeholder="Search battery by last 3 digits..."
                        searchPlaceholder="Search..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Return Battery 1 SOC (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.return_battery1_soc}
                        onChange={(e) => setFormData({ ...formData, return_battery1_soc: parseFloat(e.target.value) })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Return Battery 2 Serial No.</Label>
                      <SearchableSelect
                        value={formData.return_battery2_serial}
                        onValueChange={handleReturnBattery2Select}
                        options={batteries.map(b => ({ value: b.serial_number, label: b.serial_number }))}
                        placeholder="Search battery by last 3 digits..."
                        searchPlaceholder="Search..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Return Battery 2 SOC (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.return_battery2_soc}
                        onChange={(e) => setFormData({ ...formData, return_battery2_soc: parseFloat(e.target.value) })}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold mb-3 text-slate-700">Issued Batteries</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Issued Battery 1 Serial No. *</Label>
                      <SearchableSelect
                        value={formData.issued_battery1_serial}
                        onValueChange={handleIssuedBattery1Select}
                        options={batteries
                          .filter(b => {
                            const status = (b.status || '').toLowerCase();
                            return status === 'available' || status === 'charging';
                          })
                          .map(b => ({ value: b.serial_number, label: b.serial_number }))}
                        placeholder="Search battery by last 3 digits..."
                        searchPlaceholder="Search..."
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Issued Battery 1 SOC (%) *</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.issued_battery1_soc}
                        onChange={(e) => setFormData({ ...formData, issued_battery1_soc: parseFloat(e.target.value) })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Issued Battery 2 Serial No. *</Label>
                      <SearchableSelect
                        value={formData.issued_battery2_serial}
                        onValueChange={handleIssuedBattery2Select}
                        options={batteries
                          .filter(b => {
                            const status = (b.status || '').toLowerCase();
                            return status === 'available' || status === 'charging';
                          })
                          .map(b => ({ value: b.serial_number, label: b.serial_number }))}
                        placeholder="Search battery by last 3 digits..."
                        searchPlaceholder="Search..."
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Issued Battery 2 SOC (%) *</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.issued_battery2_soc}
                        onChange={(e) => setFormData({ ...formData, issued_battery2_soc: parseFloat(e.target.value) })}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes (Optional)</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes about this battery swap"
                    rows={3}
                  />
                </div>

                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-gradient-to-r from-lime-600 to-green-600">
                    {editingId ? 'Update' : 'Record'} Swap
                  </Button>
                </div>
              </form>
            </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              Battery Swap Records ({sortedSwaps.length}
              {totalRecords > sortedSwaps.length && ` of ${totalRecords}`})
            </CardTitle>
            {canManage && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="gap-2"
                >
                  <Filter className="w-4 h-4" />
                  {showFilters ? 'Hide Filters' : 'Show Filters'}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {showFilters && (
            <div className="mb-6 p-3 sm:p-4 bg-slate-50 rounded-lg space-y-4">
              <DateRangeFilter
                fromDate={filters.fromDate}
                toDate={filters.toDate}
                onFromDateChange={(val) => setFilters({ ...filters, fromDate: val })}
                onToDateChange={(val) => setFilters({ ...filters, toDate: val })}
              />
              <SearchFilters
                lessees={lessees}
                riders={riders}
                motorcycles={motorcycles}
                selectedLessee={filters.lessee}
                selectedRider={filters.rider}
                selectedBike={filters.bike}
                onLesseeChange={(val) => setFilters({ ...filters, lessee: val })}
                onRiderChange={(val) => setFilters({ ...filters, rider: val })}
                onBikeChange={(val) => setFilters({ ...filters, bike: val })}
              />
              {/* Location & Technician Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                <div>
                  <Label className="text-xs font-medium text-slate-500 mb-1 block">Location</Label>
                  <Select value={filters.location || '__all__'} onValueChange={(val) => setFilters({ ...filters, location: val === '__all__' ? '' : val })}>
                    <SelectTrigger data-testid="filter-location">
                      <SelectValue placeholder="All Locations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Locations</SelectItem>
                      {filterOptions.locations.map(loc => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500 mb-1 block">Technician</Label>
                  <Select value={filters.technician || '__all__'} onValueChange={(val) => setFilters({ ...filters, technician: val === '__all__' ? '' : val })}>
                    <SelectTrigger data-testid="filter-technician">
                      <SelectValue placeholder="All Technicians" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Technicians</SelectItem>
                      {filterOptions.technicians.map(tech => (
                        <SelectItem key={tech} value={tech}>{tech}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => setFilters({ fromDate: '', toDate: '', lessee: '', rider: '', bike: '', location: '', technician: '' })} data-testid="clear-filters-btn">
                  Clear Filters
                </Button>
                <Button size="sm" variant="outline" className="text-green-700 border-green-300 hover:bg-green-50" onClick={() => handleDownload('excel', false)} disabled={exporting} data-testid="download-filtered-excel">
                  <FileSpreadsheet className="w-4 h-4 mr-1" />
                  {exporting ? 'Exporting...' : 'Download Filtered (Excel)'}
                </Button>
                <Button size="sm" variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={() => handleDownload('pdf', false)} disabled={exporting} data-testid="download-filtered-pdf">
                  <FileDown className="w-4 h-4 mr-1" />
                  {exporting ? 'Exporting...' : 'Download Filtered (PDF)'}
                </Button>
              </div>
            </div>
          )}
          
          {/* Download All Records Buttons (only visible when filters are hidden) */}
          {!showFilters && (
          <div className="flex flex-wrap gap-2 mb-4">
            <Button size="sm" variant="outline" className="text-green-700 border-green-300 hover:bg-green-50" onClick={() => handleDownload('excel', true)} disabled={exporting} data-testid="download-all-excel">
              <FileSpreadsheet className="w-4 h-4 mr-1" />
              {exporting ? 'Exporting...' : 'Download All Records (Excel)'}
            </Button>
            <Button size="sm" variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={() => handleDownload('pdf', true)} disabled={exporting} data-testid="download-all-pdf">
              <FileDown className="w-4 h-4 mr-1" />
              {exporting ? 'Exporting...' : 'Download All Records (PDF)'}
            </Button>
          </div>
          )}
          
          {/* Mobile/Tablet Card View */}
          {(isMobile || isTablet) ? (
            <div className="space-y-3">
              {sortedSwaps.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                  <RefreshCw className="w-12 h-12 mb-3 text-slate-300" />
                  <p className="text-sm">No battery swaps found</p>
                </div>
              ) : (
                sortedSwaps.map((swap) => (
                  <Card 
                    key={swap.id} 
                    className="overflow-hidden active:scale-[0.99] transition-transform cursor-pointer"
                    onClick={() => handleViewDetails(swap)}
                    data-testid={`swap-card-${swap.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {/* Header - Plate & Date */}
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <PlatePair oldPlate={swap.bike_plate_number || swap.motorcycle_plate} goldenPlate={swap.bike_golden_plate_number} />
                            <Badge className="bg-green-100 text-green-700 text-xs">
                              {formatDateDisplay(swap.swap_date || swap.date) || '-'}
                            </Badge>
                          </div>

                          {/* Details Grid */}
                          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                            <div>
                              <span className="text-slate-500 text-xs">Company:</span>
                              <p className="text-slate-700 font-medium truncate">{swap.lessee_name || getLesseeName(swap.lessee_id) || '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs">ODO:</span>
                              <p className="text-slate-700">{swap.odo_reading || '-'} km</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs">Rider:</span>
                              <p className="text-slate-700 truncate">{swap.rider_name || '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs">Location:</span>
                              <p className="text-slate-700 truncate">{swap.location || swap.location_name || '-'}</p>
                            </div>
                          </div>

                          {/* Battery Info */}
                          <div className="mt-3 flex flex-wrap gap-2">
                            <div className="flex items-center gap-1">
                              <Battery className="w-3 h-3 text-red-500" />
                              <span className="text-xs font-mono bg-red-50 px-1.5 py-0.5 rounded text-red-700">
                                {swap.return_battery1_serial || swap.return_battery_1_serial || '-'}
                              </span>
                            </div>
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                            <div className="flex items-center gap-1">
                              <Battery className="w-3 h-3 text-green-500" />
                              <span className="text-xs font-mono bg-green-50 px-1.5 py-0.5 rounded text-green-700">
                                {swap.issued_battery1_serial || swap.issued_battery_1_serial || '-'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        {canManage && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-9 w-9 shrink-0"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewDetails(swap); }}>
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              {canEdit && (
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEdit(swap); }}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                              )}
                              {canDelete && (
                                <DropdownMenuItem 
                                  onClick={(e) => { e.stopPropagation(); handleDelete(swap.id); }} 
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          ) : (
            /* Desktop Table View */
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 border-b border-slate-200">
                  <TableHead className="font-semibold text-slate-700">
                    <div className="flex items-center gap-1">
                      Date & Time
                      <SortButton onSort={handleSort} currentSort={sortConfig} field="date" />
                    </div>
                  </TableHead>
                  <TableHead className="font-semibold text-slate-700">Motorcycle Plate</TableHead>
                  <TableHead className="font-semibold text-slate-700">Lessee Company</TableHead>
                  <TableHead className="font-semibold text-slate-700">Rider</TableHead>
                  <TableHead className="font-semibold text-slate-700">Technician</TableHead>
                  <TableHead className="font-semibold text-slate-700">ODO (km)</TableHead>
                  <TableHead className="font-semibold text-slate-700">Return Batteries</TableHead>
                  <TableHead className="font-semibold text-slate-700">Issued Batteries</TableHead>
                  <TableHead className="font-semibold text-slate-700">Location</TableHead>
                  {canManage && <TableHead className="font-semibold text-slate-700">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedSwaps.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canManage ? 10 : 9} className="text-center text-slate-500 py-12">
                      <div className="space-y-2">
                        <Search className="w-8 h-8 mx-auto text-slate-400" />
                        <div>No battery swaps found. Add your first swap to get started.</div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedSwaps.map((swap, index) => (
                    <TableRow 
                      key={swap.id}
                      className="cursor-pointer hover:bg-green-50 transition-colors duration-150 border-b border-slate-100"
                      onClick={() => handleViewDetails(swap)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-1 h-8 bg-gradient-to-b from-green-400 to-green-600 rounded-full"></div>
                          <div>
                            <div className="font-medium text-slate-800">{formatDateDisplay(swap.swap_date || swap.date) || '-'}</div>
                            <div className="text-xs text-slate-500">{swap.swap_time || swap.time || '-'}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-slate-800"><PlatePair oldPlate={swap.bike_plate_number || swap.motorcycle_plate} goldenPlate={swap.bike_golden_plate_number} compact /></TableCell>
                      <TableCell className="text-slate-600">{swap.lessee_name || getLesseeName(swap.lessee_id) || '-'}</TableCell>
                      <TableCell>
                        {swap.rider_name ? (
                          <div>
                            <div>{swap.rider_name}</div>
                            <div className="text-xs text-slate-500">{swap.rider_emirates_id}</div>
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {swap.technician_name || '-'}
                      </TableCell>
                      <TableCell>{swap.odo_reading || '-'}</TableCell>
                      <TableCell>
                        <div className="text-xs space-y-1">
                          {(swap.return_battery1_serial || swap.return_battery_1_serial) && (
                            <div className="bg-red-100 px-2 py-1 rounded font-mono">{swap.return_battery1_serial || swap.return_battery_1_serial} ({(swap.return_battery1_soc || swap.return_battery_1_soc || 0)}%)</div>
                          )}
                          {(swap.return_battery2_serial || swap.return_battery_2_serial) && (
                            <div className="bg-red-100 px-2 py-1 rounded font-mono">{swap.return_battery2_serial || swap.return_battery_2_serial} ({(swap.return_battery2_soc || swap.return_battery_2_soc || 0)}%)</div>
                          )}
                          {!(swap.return_battery1_serial || swap.return_battery_1_serial) && !(swap.return_battery2_serial || swap.return_battery_2_serial) && '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs space-y-1">
                          <div className="bg-green-100 px-2 py-1 rounded font-mono">{swap.issued_battery1_serial || swap.issued_battery_1_serial || '-'} ({(swap.issued_battery1_soc || swap.issued_battery_1_soc || 0)}%)</div>
                          <div className="bg-green-100 px-2 py-1 rounded font-mono">{swap.issued_battery2_serial || swap.issued_battery_2_serial || '-'} ({(swap.issued_battery2_soc || swap.issued_battery_2_soc || 0)}%)</div>
                        </div>
                      </TableCell>
                      <TableCell>{swap.location || swap.location_name || '-'}</TableCell>
                      {canManage && (
                        <TableCell>
                          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                            {canEdit && (
                              <Button size="sm" variant="ghost" onClick={() => handleEdit(swap)} className="hover:bg-green-100">
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button size="sm" variant="ghost" onClick={() => handleDelete(swap.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          )}
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="text-sm text-slate-500">
                Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, totalRecords)} of {totalRecords} records
              </div>
              <div className="flex items-center gap-1">
                {/* First page */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1 || loading}
                  className="px-2"
                >
                  {'<<'}
                </Button>
                
                {/* Previous page */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  className="px-2"
                >
                  {'<'}
                </Button>
                
                {/* Page numbers */}
                {getPageNumbers().map((page, index) => (
                  page === '...' ? (
                    <span key={`ellipsis-${index}`} className="px-2 text-slate-400">...</span>
                  ) : (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(page)}
                      disabled={loading}
                      className={`px-3 ${currentPage === page ? 'bg-green-600 hover:bg-green-700' : ''}`}
                    >
                      {page}
                    </Button>
                  )
                ))}
                
                {/* Next page */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                  className="px-2"
                >
                  {'>'}
                </Button>
                
                {/* Last page */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages || loading}
                  className="px-2"
                >
                  {'>>'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-3xl'} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>Battery Swap Details</DialogTitle>
            <DialogDescription>
              Complete information about this battery swap transaction
            </DialogDescription>
          </DialogHeader>
          {selectedSwap && (
            <div className="space-y-6">
              {/* Swap Information */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-slate-700">Swap Information</h3>
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <p className="text-sm text-slate-500">Date</p>
                    <p className="font-medium">{formatDateDisplay(selectedSwap.swap_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Time</p>
                    <p className="font-medium">{selectedSwap.swap_time}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Location</p>
                    <p className="font-medium">{selectedSwap.location || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">ODO Reading</p>
                    <p className="font-medium">{selectedSwap.odo_reading} km</p>
                  </div>
                </div>
              </div>

              {/* Motorcycle & Rider Information */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-slate-700">Motorcycle & Rider</h3>
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <p className="text-sm text-slate-500">Motorcycle Plate</p>
                    <div className="mt-1"><PlatePair oldPlate={selectedSwap.bike_plate_number || selectedSwap.motorcycle_plate} goldenPlate={selectedSwap.bike_golden_plate_number} /></div>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Rider Name</p>
                    <p className="font-medium">{selectedSwap.rider_name || 'N/A'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-slate-500">Rider Emirates ID</p>
                    <p className="font-medium">{selectedSwap.rider_emirates_id || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Return Batteries */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-slate-700">Return Batteries</h3>
                <div className="space-y-3">
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <h4 className="font-medium text-red-900 mb-2">Battery 1</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-sm text-red-700">Serial Number</p>
                        <p className="font-medium text-red-900">{selectedSwap.return_battery1_serial || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-red-700">State of Charge (SOC)</p>
                        <p className="font-medium text-red-900">{selectedSwap.return_battery1_soc}%</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <h4 className="font-medium text-red-900 mb-2">Battery 2</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-sm text-red-700">Serial Number</p>
                        <p className="font-medium text-red-900">{selectedSwap.return_battery2_serial || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-red-700">State of Charge (SOC)</p>
                        <p className="font-medium text-red-900">{selectedSwap.return_battery2_soc}%</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Issued Batteries */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-slate-700">Issued Batteries</h3>
                <div className="space-y-3">
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h4 className="font-medium text-green-900 mb-2">Battery 1</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-sm text-green-700">Serial Number</p>
                        <p className="font-medium text-green-900">{selectedSwap.issued_battery1_serial}</p>
                      </div>
                      <div>
                        <p className="text-sm text-green-700">State of Charge (SOC)</p>
                        <p className="font-medium text-green-900">{selectedSwap.issued_battery1_soc}%</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h4 className="font-medium text-green-900 mb-2">Battery 2</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-sm text-green-700">Serial Number</p>
                        <p className="font-medium text-green-900">{selectedSwap.issued_battery2_serial}</p>
                      </div>
                      <div>
                        <p className="text-sm text-green-700">State of Charge (SOC)</p>
                        <p className="font-medium text-green-900">{selectedSwap.issued_battery2_soc}%</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedSwap.notes && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-slate-700">Notes</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-slate-700">{selectedSwap.notes}</p>
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mismatch Dialog */}
      <Dialog open={mismatchDialogOpen} onOpenChange={setMismatchDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-3xl'} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertCircle className="w-5 h-5" />
              Battery Mismatch Detected!
            </DialogTitle>
            <DialogDescription>
              The returned batteries don&apos;t match the expected batteries. Please provide details and upload photos.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {mismatchData && (
              <Alert className="bg-orange-50 border-orange-200">
                <AlertDescription>
                  <div className="space-y-2">
                    <div>
                      <span className="font-semibold">Expected Batteries:</span>
                      <div className="ml-4 mt-1">
                        {mismatchData.expected.map((serial, i) => (
                          <div key={i} className="font-mono text-sm">• {serial}</div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <span className="font-semibold">Actual Batteries Returned:</span>
                      <div className="ml-4 mt-1">
                        {mismatchData.actual.map((serial, i) => (
                          <div key={i} className="font-mono text-sm text-orange-600">• {serial}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <div>
              <Label>Description of Mismatch *</Label>
              <Textarea
                value={mismatchForm.description}
                onChange={(e) => setMismatchForm({ ...mismatchForm, description: e.target.value })}
                placeholder="Explain why the batteries don't match..."
                rows={4}
                required
              />
            </div>

            <div className="space-y-3">
              <Label>Upload Photos (5 required) *</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  'Bike Number Plate',
                  'Discharged Battery 1',
                  'Discharged Battery 2',
                  'Charged Battery 1',
                  'Charged Battery 2'
                ].map((label, index) => (
                  <div key={index} className="space-y-2">
                    <Label className="text-xs text-slate-600">{label}</Label>
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => handlePhotoUpload(e, index)}
                        className="hidden"
                        id={`mismatch-photo-${index}`}
                      />
                      <label
                        htmlFor={`mismatch-photo-${index}`}
                        className="flex flex-col items-center justify-center h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-slate-50"
                      >
                        {mismatchForm.photos[index] ? (
                          <img
                            src={mismatchForm.photos[index].preview}
                            alt={label}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        ) : (
                          <>
                            <Camera className="w-8 h-8 text-slate-400" />
                            <span className="text-xs text-slate-500 mt-2">Tap to capture</span>
                          </>
                        )}
                      </label>
                      {mismatchForm.photos[index] && (
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          className="absolute top-1 right-1"
                          onClick={() => {
                            const newPhotos = [...mismatchForm.photos];
                            newPhotos[index] = null;
                            setMismatchForm({ ...mismatchForm, photos: newPhotos });
                          }}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setMismatchDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleMismatchSubmit} 
                disabled={loading}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {loading ? 'Submitting...' : 'Submit Mismatch Report'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Alerts List Dialog */}
      <Dialog open={alertsDialogOpen} onOpenChange={setAlertsDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-5xl'} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              Battery Mismatch Alerts ({mismatchAlerts.length})
            </DialogTitle>
            <DialogDescription>
              Review and resolve battery mismatch incidents
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {mismatchAlerts.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Pending Alerts</h3>
                <p className="text-slate-600">All battery mismatch incidents have been resolved!</p>
              </div>
            ) : (
              mismatchAlerts.map(alert => (
                <Card key={alert.id} className="border-orange-300 bg-orange-50">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="font-semibold text-lg">{alert.rider_name}</div>
                        <div className="text-sm text-slate-600">Bike: {alert.bike_number} | Emirates ID: {alert.emirates_id}</div>
                        <div className="text-xs text-slate-500">{formatDateTimeDisplay(alert.created_at)}</div>
                      </div>
                      <Button size="sm" onClick={() => handleViewAlert(alert)}>
                        <Eye className="w-4 h-4 mr-1" />
                        View Details
                      </Button>
                    </div>

                    <Alert className="bg-orange-100 border-orange-300">
                      <AlertDescription>
                        <div className="grid md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <div className="font-semibold mb-1">Expected:</div>
                            {alert.expected_batteries.map((b, i) => <div key={i} className="font-mono">• {b}</div>)}
                          </div>
                          <div>
                            <div className="font-semibold mb-1">Returned:</div>
                            {alert.actual_batteries.map((b, i) => <div key={i} className="font-mono text-red-600">• {b}</div>)}
                          </div>
                        </div>
                      </AlertDescription>
                    </Alert>

                    {alert.photos && alert.photos.length > 0 && (
                      <div className="mt-3">
                        <div className="text-sm font-semibold mb-2">Photos:</div>
                        <div className="grid grid-cols-5 gap-2">
                          {alert.photos.map((photo, i) => (
                            <img
                              key={i}
                              src={photo}
                              alt={`Photo ${i + 1}`}
                              className="w-full aspect-square object-cover rounded cursor-pointer hover:opacity-75"
                              onClick={() => {
                                setSelectedImage(photo);
                                setImageDialogOpen(true);
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-4xl'}`}>
          <DialogHeader>
            <DialogTitle>Photo View</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center">
            <img
              src={selectedImage}
              alt="Full size"
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* -------------------------------------------------------------
          ANOMALY BLOCK DIALOG — shown when backend rejects a swap due
          to Typo Spike or Missing Reading. Big red header, giant
          typography — designed for low-literacy technicians. Covers
          ~40% of screen width.
          ------------------------------------------------------------- */}
      <Dialog
        open={!!anomalyBlock}
        onOpenChange={(o) => { if (!o) { setAnomalyBlock(null); setOverrideReason(''); } }}
      >
        <DialogContent
          className="max-w-[520px] w-[92vw] p-0 overflow-hidden border-0 rounded-2xl"
          data-testid="swap-anomaly-block-dialog"
        >
          {anomalyBlock && (
            <div className="bg-white">
              {/* Header — red for hard blocks, amber for the reset confirm */}
              <div
                className={`text-white p-5 ${
                  anomalyBlock.code === 'odo_reset_confirm' ? 'bg-amber-500' : 'bg-rose-600'
                }`}
              >
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-9 h-9 shrink-0 mt-0.5" strokeWidth={2.5} />
                  <div>
                    <div className="text-xs uppercase tracking-wide opacity-90">
                      {anomalyBlock.code === 'missing_reading'
                        ? 'Missing ODO reading'
                        : anomalyBlock.code === 'odo_reset_confirm'
                          ? 'ODO reset detected'
                          : 'Wrong ODO reading'}
                    </div>
                    <DialogTitle className="text-2xl font-bold leading-tight mt-1">
                      {anomalyBlock.title}
                    </DialogTitle>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <p className="text-base text-slate-800 leading-relaxed">
                  {anomalyBlock.message}
                </p>

                <div className="grid grid-cols-2 gap-3 rounded-lg border-2 border-slate-200 p-4 bg-slate-50">
                  <div>
                    <div className="text-[11px] uppercase text-slate-500 tracking-wide">Last reading</div>
                    <div className="text-2xl font-bold text-slate-900 tabular-nums">
                      {Number(anomalyBlock.previous_odo || 0).toLocaleString()} km
                    </div>
                  </div>
                  <div>
                    <div className={`text-[11px] uppercase tracking-wide ${
                      anomalyBlock.code === 'odo_reset_confirm' ? 'text-amber-600' : 'text-rose-600'
                    }`}>You entered</div>
                    <div className={`text-2xl font-bold tabular-nums ${
                      anomalyBlock.code === 'odo_reset_confirm' ? 'text-amber-600' : 'text-rose-600'
                    }`}>
                      {anomalyBlock.code === 'missing_reading'
                        ? '—'
                        : `${Number(anomalyBlock.current_odo || 0).toLocaleString()} km`}
                    </div>
                  </div>
                </div>

                {anomalyBlock.code === 'odo_reset_confirm' ? (
                  <>
                    {/* Two-button confirm layout — Confirm (amber) + Correct (dark) */}
                    <Button
                      className="w-full h-12 text-base bg-amber-500 hover:bg-amber-600 text-white"
                      onClick={() => saveSwap({ odo_reset_confirmed: true })}
                      data-testid="swap-odo-reset-confirm-btn"
                    >
                      Yes, ODO was reset — Continue
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full h-11 text-sm"
                      onClick={() => { setAnomalyBlock(null); setOverrideReason(''); }}
                      data-testid="swap-odo-reset-cancel-btn"
                    >
                      No — Let me correct the reading
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      className="w-full h-12 text-base bg-slate-900 hover:bg-slate-800 text-white"
                      onClick={() => { setAnomalyBlock(null); setOverrideReason(''); }}
                      data-testid="swap-anomaly-correct-btn"
                    >
                      Correct the ODO reading
                    </Button>

                    <details className="text-xs text-slate-500">
                      <summary className="cursor-pointer hover:text-slate-700">
                        I&apos;m sure it&apos;s correct — save anyway
                      </summary>
                      <div className="mt-2 space-y-2 pl-2 border-l-2 border-slate-200">
                        <p className="text-[11px] text-slate-600">
                          Please explain why this reading is correct. A supervisor may review this later.
                        </p>
                        <Textarea
                          value={overrideReason}
                          onChange={(e) => setOverrideReason(e.target.value)}
                          placeholder="Reason (required to override)"
                          className="text-xs min-h-[60px]"
                          data-testid="swap-anomaly-reason"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!overrideReason.trim()}
                          onClick={() => saveSwap({ force_save: true, force_save_reason: overrideReason.trim() })}
                          className="w-full text-xs border-rose-300 text-rose-600 hover:bg-rose-50"
                          data-testid="swap-anomaly-force-save-btn"
                        >
                          Save anyway
                        </Button>
                      </div>
                    </details>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BatterySwaps;